/*
 * `GET /v1/me/capabilities` — the membership envelope.
 *
 * Capability *gating* does not read any of this. Capabilities reach the browser
 * in the `roles_token` cookie and resolve synchronously through
 * `CapabilitiesService`; that path is untouched. This endpoint answers a
 * different question — **which screen should the app render for this person in
 * this partner area** — because an empty capability set means four separate
 * things and none of them are distinguishable from the token alone.
 */

import type { Capability } from '../model/capabilities';
import type { SeatStatus } from '../model/roles';

/**
 * A user's standing in one partner area.
 *
 * | state       | meaning                                   |
 * | ----------- | ----------------------------------------- |
 * | `active`    | role assigned, capabilities populated     |
 * | `no_role`   | active seat, no role                      |
 * | `suspended` | seat paused by an admin                   |
 * | `invited`   | seat not yet accepted                     |
 * | `none`      | no membership edge                        |
 * | `admin`     | superAdmin browsing without a membership  |
 *
 * `suspended` and `no_role` are deliberately distinct: both resolve to an empty
 * capability set, and they mean opposite things — "wait for a role" versus
 * "someone removed your access".
 */
export type PartnerMembershipState =
  | 'active'
  | 'no_role'
  | 'suspended'
  | 'invited'
  | 'none'
  | 'admin';

/** Every state, at runtime, so the guard below cannot drift from the type. */
export const PARTNER_MEMBERSHIP_STATES = [
  'active',
  'no_role',
  'suspended',
  'invited',
  'none',
  'admin',
] as const satisfies readonly PartnerMembershipState[];

const STATE_SET: ReadonlySet<unknown> = new Set(PARTNER_MEMBERSHIP_STATES);

/**
 * Type guard for a state string off the wire. Anything unrecognised — a value
 * added by a future backend, a typo, a missing field — must fall through to
 * "render the app normally" rather than blocking someone out of it.
 */
export function isPartnerMembershipState(value: unknown): value is PartnerMembershipState {
  return STATE_SET.has(value);
}

/**
 * The response body, scoped to the active partner by the `creatorSub` header
 * the auth interceptor already sends.
 *
 * `membership` and everything after it are **optional in the type on purpose**.
 * This shipped before the backend field landed, and the endpoint answered
 * `{creatorId, capabilities}` alone for a while. Typing them as required would
 * have pushed the `undefined` handling into a cast; optional makes every
 * consumer confront it.
 */
export interface MeCapabilitiesResponse {
  creatorId: string;
  capabilities: Capability[];
  membership?: PartnerMembershipState;
  roleId?: string | null;
  roleName?: string | null;
  seatStatus?: SeatStatus | null;
  /**
   * The area's name and its owner's address — the copy for the no-role and
   * paused screens (HAL-51), neither of which can get either field from
   * `GET /v1/me/partners`: that route lists only the areas the caller holds an
   * *active* seat in, so it omits the area for the very user the paused screen
   * is for.
   *
   * Both are `null` when `membership` is `none`. A deliberate gate, not a gap —
   * this route has no scope check, so populating them unconditionally would
   * answer any `?creatorId=` anyone cared to walk with a partner's name and
   * their owner's email address. A membership edge of any seat status
   * (`active`, `suspended`, `invited`) is enough, as is a superAdmin, so every
   * state that renders one of those screens gets them.
   *
   * `ownerEmail` is separately `null` when the area has no resolvable owner.
   * The screens do not tell the two apart: either way there is nobody to name.
   */
  partnerName?: string | null;
  ownerEmail?: string | null;
}

/**
 * The states that replace the app with a whole-page message.
 *
 * An allow-list rather than a chain of `!==`, so `admin`, `active`, `none` and
 * `invited` are non-blocking *by construction*. Adding a screen later means
 * adding to this array and adding a `@case` — never touching the negative
 * space, where a missed state locks someone out.
 *
 * `invited` is not here by decision, not omission: an unaccepted seat has no
 * screen of its own yet.
 */
export const BLOCKING_MEMBERSHIP_STATES = ['no_role', 'suspended'] as const;

/** A membership state that stops the app rendering. */
export type BlockingMembershipState = (typeof BLOCKING_MEMBERSHIP_STATES)[number];

const BLOCKING_SET: ReadonlySet<unknown> = new Set(BLOCKING_MEMBERSHIP_STATES);

/** Type guard: does this state replace the app with a message? */
export function isBlockingMembershipState(value: unknown): value is BlockingMembershipState {
  return BLOCKING_SET.has(value);
}
